/**
 * activeInputSpace — the single source of truth for "where does pointer input
 * live right now": normal (paper / model) space, or redirected into a paper-space
 * VIEWPORT's model space (MSPACE_VIA_VP).
 *
 * **THREE-free by design** (same contract as `viewportTransform2d`). It carries
 * only the planar gate + the `paperToModel` / `modelToPaper` affine, the
 * document↔render frame pair, and a minimal STRUCTURAL view of the redirect
 * context, so the three pointer paths (pick / snap / draft, wired in later MC4
 * slices) and the wheel/pan path can all consult ONE resolver. It deliberately
 * imports nothing from `three`; the caller supplies the already-unprojected
 * `paperPt` and the VP on-screen pixel height (`vpScreenHeightPx`, which needs
 * the OrthographicCamera) as plain numbers. (`../coordinateOrigin` is a leaf
 * value module whose only import is a TYPE-ONLY `Vec3`, so it pulls no
 * runtime `three` in either.)
 *
 * The gate here is byte-identical in semantics to
 * `stageInteractions.resolveViewportRedirect` — which now delegates to this
 * module so the gate + transform logic is single-sourced (CLAUDE.md PRIORITY #2,
 * dep-graph: no duplicated decision logic between wheel/pan and the pointer paths).
 *
 * AutoCAD parity: a wheel/pan/pick over an UNLOCKED active viewport in
 * MSPACE_VIA_VP acts on the VP's model view; over a LOCKED VP (or outside its
 * paper rect, or in PSPACE / plain MSPACE) it falls through to normal space.
 */

import {
  applyOffsetToPoint2d,
  unapplyOffsetToPoint2d,
} from "../coordinateOrigin";
import {
  modelToPaper,
  paperToModel,
  pointInViewport,
  type PaperOrigin,
  type Point2D,
  type ViewportLike,
} from "./viewportTransform2d";

/**
 * Minimal STRUCTURAL view of the redirect context the gate needs.
 *
 * Declared here (not imported from `stageInteractions`) so this THREE-free
 * module never pulls `three` in through that file. The real
 * `ViewportRedirectContext` in `stageInteractions.ts` is a structural supertype
 * of this interface (same field names + shapes), so it is assignable here with
 * no adapter.
 */
export interface ViewportRedirectContextLike {
  /** Current space-context mode. */
  readonly mode: "PSPACE" | "MSPACE" | "MSPACE_VIA_VP";
  /** Active VP id (null unless MSPACE_VIA_VP). */
  readonly activeViewportId: string | null;
  /**
   * Resolve the active VP's view params + paper rect (superset of
   * `ViewportLike` plus `id`, the effective lock flag, and the per-VP
   * `frozenLayers` list). Returns null when the id no longer resolves (e.g. VP
   * deleted mid-gesture). `frozenLayers` (MC4 S2 P2 #D) lets the pick/drag paths
   * exclude candidates frozen in the active VP; optional for callers that omit it.
   */
  readonly getActiveViewport: () =>
    | (ViewportLike & {
        id: string;
        effectiveLocked: boolean;
        frozenLayers?: readonly string[];
      })
    | null;
  /**
   * Paper-space sheet origin for the VP transform, in the renderer's
   * OFFSET-world frame (`Scene3DStage`'s `paperOriginOffset` —
   * `coordinateOrigin.applyOffsetToPaperOrigin(pageSetup.origin)`), matching
   * the offset-world `paperPt` the pointer paths resolve via `clientToWorld`.
   * `toSpace`/`paperToModel` still returns a DOCUMENT-frame model point (it
   * adds the document-frame WCS view centre — `viewTarget + R(twist)·viewCenter` — to
   * a frame-free delta), which is what makes the in-VP draft commit correct
   * without any un-offset: a drafted entity's geometry is WCS model space,
   * exactly what comes out.
   *
   * The `viewport-set-view` write-back is the ONE exception, and it is a
   * FRAME question, not an ORIGIN one: the entity's `viewCenter` field is
   * DCS, so a WCS point from `toSpace` must lose the target
   * (`viewportStoredViewCenterFromWcs`) before it is stored. See
   * `viewportTransform2d`'s frame contract.
   */
  readonly paperOrigin: PaperOrigin;
}

/**
 * The resolved input space for one pointer position. Inside an eligible VP it
 * maps paper ↔ model; outside (the identity case) every transform is a no-op and
 * only the caller's fallback (active) space is a valid candidate.
 */
export interface ActiveInputSpace {
  /** `"model"` inside a VP; otherwise the caller's fallback space id. */
  readonly spaceId: string;
  /** paper → active space: `paperToModel` inside a VP; identity outside. */
  toSpace(paperPt: Point2D): Point2D;
  /** active space → paper: `modelToPaper` inside a VP; identity outside. */
  fromSpace(spacePt: Point2D): Point2D;
  /**
   * active space → the DOCUMENT (AutoCAD/DXF) frame, and {@link fromDocument}
   * back. This is the ONE place that knows which frame `toSpace` produced,
   * and therefore the only honest home for the conversion.
   *
   * ## Why the two branches differ
   *
   * `toSpace` is NOT frame-uniform:
   *
   *   - **identity** (plain model / paper space, locked VP, cursor outside an
   *     eligible VP) — `toSpace` is a no-op over `paperPt`, which the caller
   *     unprojected with `stageCameraMath.clientToWorld` against the
   *     OFFSET-world render camera. The result is an OFFSET-world point, so
   *     `toDocument` must add the session origin back.
   *   - **redirected** (MSPACE_VIA_VP over an eligible VP) — `toSpace` is
   *     `paperToModel`, which adds the DOCUMENT-frame WCS view centre
   *     (`viewTarget + R(twist)·viewCenter`) to a frame-free delta (see
   *     `viewportTransform2d`'s `PaperOrigin` doc).
   *     Its result is ALREADY a document-frame model point, so `toDocument`
   *     is the identity here. Un-offsetting it as well would push `+O` into
   *     a persisted field — the corruption this pair exists to prevent.
   *
   * ## Who uses it
   *
   * The pick loop, and only at the two points where a coordinate LEAVES the
   * pointer pipeline for the application: the `onCanvasPoint` commit and the
   * `onCanvasHover` emit (which also feeds the DI cursor snapshot). Everything
   * upstream of those two calls — the spatial query, the OSNAP resolve, the
   * overlay marker projection, the OTRACK acquired-point store — stays in the
   * offset-world/snap frame and is untouched.
   *
   * {@link fromDocument} is the exact inverse and exists for the one value
   * that travels the other way: the draft-aid ANCHOR
   * (`StageInteractionBindings.orthoBasePointRef`), which `Scene3DStage`
   * derives from the draft session's own (document-frame) points and which
   * the ORTHO/POLAR constraint math must see co-space with the emit.
   */
  toDocument(spacePt: Point2D): Point2D;
  /** DOCUMENT frame → active space. Exact inverse of {@link toDocument}. */
  fromDocument(documentPt: Point2D): Point2D;
  /**
   * active space → the renderer's **OFFSET-WORLD** frame, and
   * {@link fromOffsetWorld} back. Offset-world is the frame of the SPATIAL
   * INDEX, of every adapter-built `RenderableEntity.geometry`, and of the
   * THREE cameras — one frame, no per-space rule
   * (`state/spatialBuilder.ts buildSpatialRecordsForSpaces`,
   * `coordinateOrigin.applyOffsetToBounds`). Anything handed to
   * `SpatialIndex.intersect` / `.pick`, to `selectInWindow`, to
   * `refineEntityPick` / `refinePickCandidates` or to `resolveObjectSnap`
   * must come through here first, and anything read back OUT of those
   * (an OSNAP hit's `world`, a candidate's geometry point) must come back
   * through {@link fromOffsetWorld} before it meets {@link fromSpace},
   * {@link toDocument} or the emit cursor.
   *
   * ## Why the two branches differ
   *
   * Exactly as in {@link toDocument} — `toSpace` is NOT frame-uniform, and
   * this pair is the other half of that same truth:
   *
   *   - **identity** (plain model / paper space, locked VP, cursor outside an
   *     eligible VP) — `toSpace` is a no-op over the OFFSET-world `paperPt`
   *     the caller unprojected with `stageCameraMath.clientToWorld`. The
   *     active space IS the offset-world frame, so BOTH directions are the
   *     identity here.
   *   - **redirected** (MSPACE_VIA_VP over an eligible VP) — `toSpace` is
   *     `paperToModel`, whose result is already a DOCUMENT-frame model point.
   *     The index holds the SAME model geometry at `v − O`, so the query
   *     point must be offset (`applyOffsetToPoint2d`) and a hit read back out
   *     of it un-offset. Skipping this is the "every in-VP pick / hover /
   *     OSNAP / window-select misses by the whole session origin" defect.
   *
   * The two pairs compose exactly, on BOTH branches:
   * `toOffsetWorld(p) === applyOffsetToPoint2d(toDocument(p))`. That identity
   * is what makes the document frame and the render frame one origin apart
   * no matter which branch the pointer is on, and it is pinned by
   * `__tests__/activeInputSpace.frames.test.ts`.
   */
  toOffsetWorld(spacePt: Point2D): Point2D;
  /**
   * OFFSET-WORLD (spatial-index / render) frame → active space. Exact inverse
   * of {@link toOffsetWorld}.
   */
  fromOffsetWorld(offsetWorldPt: Point2D): Point2D;
  /**
   * Pixels per active-space world unit. Inside a VP this is
   * `vpScreenHeightPx / vp.viewHeight` (with `vpScreenHeightPx` derived from
   * `vp.height × paperPixelsPerWorld` when the caller does not supply it);
   * outside it is the paper camera's `paperPixelsPerWorld` (passed unchanged).
   * Used by pick/snap to size the model-space aperture/tolerance.
   */
  readonly pixelsPerWorld: number;
  /**
   * Whether an entity in `entitySpaceId` is a valid pick/snap candidate here.
   * Inside a VP only model-space entities qualify; in the identity case only
   * the caller's fallback (active) space qualifies. The identity filter matters
   * on a layout: `SceneBulkLoad` appends hidden MODEL-space entities into the
   * SAME shared spatial index, so an always-true predicate would let a raw
   * paper/layout pick hit invisible model geometry whose bounds overlap the
   * sheet. In normal MODEL space `fallbackSpaceId === "model"`, so model
   * entities still pass.
   */
  candidateEligible(entitySpaceId: string): boolean;
  /**
   * The active VP (incl. its `id` and per-VP `frozenLayers`) when redirected;
   * null in the identity case. `frozenLayers` (MC4 S2 P2 #D) is surfaced so the
   * in-VP pick/drag paths can drop candidates frozen in the active viewport.
   */
  readonly viewport:
    | (ViewportLike & { id: string; frozenLayers?: readonly string[] })
    | null;
}

/** Identity (no-redirect) input space — paper/model space, no VP transform. */
function identitySpace(
  fallbackSpaceId: string,
  paperPixelsPerWorld: number,
): ActiveInputSpace {
  return {
    spaceId: fallbackSpaceId,
    toSpace: (paperPt) => paperPt,
    fromSpace: (spacePt) => spacePt,
    // Identity space === the OFFSET-world render frame (`clientToWorld`
    // unprojects against the offset-world camera), so the document frame is
    // one origin away. See `ActiveInputSpace.toDocument`'s doc.
    toDocument: (spacePt) => unapplyOffsetToPoint2d(spacePt),
    fromDocument: (documentPt) => applyOffsetToPoint2d(documentPt),
    // Identity space IS the offset-world render/index frame, so the spatial
    // query needs no conversion at all. See `toOffsetWorld`'s doc.
    toOffsetWorld: (spacePt) => ({ x: spacePt.x, y: spacePt.y }),
    fromOffsetWorld: (offsetWorldPt) => ({
      x: offsetWorldPt.x,
      y: offsetWorldPt.y,
    }),
    pixelsPerWorld: paperPixelsPerWorld,
    // Identity mode accepts ONLY the active (fallback) space. On a layout the
    // shared spatial index also holds hidden MODEL-space buckets; without this
    // filter a raw paper/layout pick could snap to invisible model geometry
    // whose bounds overlap the sheet. In normal MODEL space fallbackSpaceId is
    // "model", so model entities still pass.
    candidateEligible: (entitySpaceId) => entitySpaceId === fallbackSpaceId,
    viewport: null,
  };
}

/**
 * Resolve the active input space for a pointer at `paperPt`.
 *
 * The gate matches `resolveViewportRedirect` exactly: redirect ONLY when
 * `mode === "MSPACE_VIA_VP"`, `activeViewportId != null`, the VP resolves and
 * its id matches, the VP is UNLOCKED, `height > 0 && viewHeight > 0`, and the
 * cursor's `paperPt` is inside the VP's (axis-aligned) paper rect. Any failure →
 * identity space.
 *
 * **`ignoreCursorRect`** (default `false`) — when `true`, the LAST gate (the
 * `pointInViewport(paperPt, vp, paperOrigin)` over-rect hit-test) is SKIPPED;
 * every other gate (mode, active-id match, VP resolves, NOT locked,
 * `height > 0 && viewHeight > 0`) is preserved unchanged. So over an UNLOCKED
 * active VP in MSPACE_VIA_VP it ALWAYS returns the model-space mapping
 * (`toSpace = paperToModel`, `spaceId = "model"`, model-only `candidateEligible`)
 * regardless of where the cursor sits — `paperToModel` is well-defined for any
 * paper point. This is MC4 edge case #2: while a draft is in progress the cursor's
 * draft point must stay in the VP MODEL space even past the VP edge, so the rubber
 * band spans the frame and the render-side clip cuts it at the boundary (AutoCAD
 * floating-MSPACE parity). The pick/snap paths leave it `false`, so they keep
 * gating on the cursor rect (a pick OUTSIDE the rect still falls through to the
 * identity/paper space). With `false` this function is byte-identical to its
 * pre-`ignoreCursorRect` behaviour.
 */
export function resolveActiveInputSpace(args: {
  paperPt: Point2D;
  paperPixelsPerWorld: number;
  redirect: ViewportRedirectContextLike | null;
  fallbackSpaceId: string;
  vpScreenHeightPx?: number;
  /**
   * Skip ONLY the cursor-over-rect `pointInViewport` gate (default `false`).
   * See the function doc: used by the in-progress-draft pointer path so the
   * draft point stays in VP model space even outside the rect. Every other gate
   * is still enforced. `false` ⇒ existing behaviour byte-for-byte.
   */
  ignoreCursorRect?: boolean;
}): ActiveInputSpace {
  const {
    paperPt,
    paperPixelsPerWorld,
    redirect,
    fallbackSpaceId,
    vpScreenHeightPx,
    ignoreCursorRect = false,
  } = args;

  // ── Gate (byte-identical to resolveViewportRedirect) ─────────────────────
  if (!redirect) return identitySpace(fallbackSpaceId, paperPixelsPerWorld);
  if (redirect.mode !== "MSPACE_VIA_VP" || redirect.activeViewportId == null) {
    return identitySpace(fallbackSpaceId, paperPixelsPerWorld);
  }
  const vp = redirect.getActiveViewport();
  if (!vp || vp.id !== redirect.activeViewportId) {
    return identitySpace(fallbackSpaceId, paperPixelsPerWorld);
  }
  // Locked VP → input acts on the paper (AutoCAD parity); do NOT redirect.
  if (vp.effectiveLocked) {
    return identitySpace(fallbackSpaceId, paperPixelsPerWorld);
  }
  if (vp.height <= 0 || vp.viewHeight <= 0) {
    return identitySpace(fallbackSpaceId, paperPixelsPerWorld);
  }
  // Over-VP hit-test in paper space (the VP frame is axis-aligned). SKIPPED
  // only when `ignoreCursorRect` (in-progress draft, MC4 edge case #2): the
  // draft point must keep mapping into VP model space past the VP edge so the
  // rubber band spans the frame and the render-side clip trims it. Every OTHER
  // gate above still holds, so a locked VP / wrong mode / no active VP still
  // falls through to identity even with `ignoreCursorRect:true`.
  if (!ignoreCursorRect && !pointInViewport(paperPt, vp, redirect.paperOrigin)) {
    return identitySpace(fallbackSpaceId, paperPixelsPerWorld);
  }

  // ── Redirected: map paper ↔ this VP's model space ─────────────────────────
  const paperOrigin = redirect.paperOrigin;
  // VP on-screen pixel height. The wheel/pan path already has it and passes it;
  // any other caller (pick / snap / draft) may omit it, in which case derive it
  // from the VP's paper height × the paper camera's pixels-per-world (the
  // identical quantity) so every caller gets a valid model-space
  // `pixelsPerWorld` without recomputing the camera frustum. `vp.height > 0` is
  // guaranteed by the gate above, so this is always finite and > 0.
  const effectiveVpScreenHeightPx =
    vpScreenHeightPx ?? vp.height * paperPixelsPerWorld;
  const pixelsPerWorld = effectiveVpScreenHeightPx / vp.viewHeight;
  const { id, ...viewportLike } = vp;
  return {
    spaceId: "model",
    toSpace: (pt) => paperToModel(pt, vp, paperOrigin),
    fromSpace: (pt) => modelToPaper(pt, vp, paperOrigin),
    // `paperToModel` adds the DOCUMENT-frame WCS view centre (`viewTarget +
    // viewCenter`) to a frame-free delta, so its output is already a document
    // model point — both directions are the identity here. See `toDocument`'s
    // doc for why adding `unapplyOffset` on top would corrupt a persisted
    // field, and for the separate DCS un-offset the `viewCenter` write-back
    // (and only that write-back) still owes.
    toDocument: (pt) => ({ x: pt.x, y: pt.y }),
    fromDocument: (pt) => ({ x: pt.x, y: pt.y }),
    // ...and for exactly that reason the SPATIAL INDEX is a whole origin
    // away from `toSpace`'s output here: the index stores the same model
    // geometry at `v − O` (`state/spatialBuilder.ts`). Every query point
    // crosses here on the way in, every hit on the way back out.
    toOffsetWorld: (pt) => applyOffsetToPoint2d(pt),
    fromOffsetWorld: (pt) => unapplyOffsetToPoint2d(pt),
    pixelsPerWorld,
    candidateEligible: (entitySpaceId) => entitySpaceId === "model",
    viewport: { ...viewportLike, id },
  };
}
